
import { useState, useEffect } from 'react';
import { getFeaturedPrompts } from '@/services/promptService';
import { getPopularPrompts } from '@/utils/promptUtils';
import { toast } from 'sonner';
import type { Prompt } from '@/services/promptService';

/**
 * Custom hook that fetches popular prompts with the same algorithm used in explore page
 */
export function usePopularPrompts(limit: number = 8) {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchPrompts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log(`Fetching popular prompts with limit: ${limit} (attempt: ${retryCount + 1})`);
      
      // Try to get prompts using the ranking algorithm from utils
      let popularPrompts = await getPopularPrompts(limit);
      
      // Log the result to help debugging
      console.log(`Popular prompts result:`, popularPrompts ? 
        `${popularPrompts.length} prompts found` : 'no prompts found');
      
      // If no popular prompts, fall back to featured prompts
      if (!popularPrompts || popularPrompts.length === 0) {
        console.log('No popular prompts found, falling back to featured prompts');
        const featuredPrompts = await getFeaturedPrompts();
        console.log('Featured prompts result:', featuredPrompts ? 
          `${featuredPrompts.length} prompts found` : 'no prompts found');
        
        popularPrompts = featuredPrompts || [];
      }
      
      if (popularPrompts.length > 0) {
        setPrompts(popularPrompts);
      } else if (retryCount < 2) {
        // If still no prompts and we haven't tried too many times, schedule a retry
        console.log(`No prompts found after attempt ${retryCount + 1}, will retry in 2 seconds`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 2000);
      } else {
        // After multiple attempts with no prompts, show a toast
        toast.error('Unable to load prompts. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching popular prompts:', err);
      setError(err instanceof Error ? err : new Error('Unknown error fetching prompts'));
      
      if (retryCount < 2) {
        // Schedule a retry on error
        console.log(`Error fetching prompts (attempt ${retryCount + 1}), will retry in 2 seconds`);
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, 2000);
      } else {
        toast.error('Failed to load prompts after multiple attempts');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, [limit, retryCount]);

  return { 
    prompts, 
    isLoading, 
    error,
    refetch: () => {
      setRetryCount(0);
      fetchPrompts();
    }
  };
}
